import * as amplitude from '@amplitude/analytics-browser';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';

import {
  COMMENTS_SCROLL,
  QUERY_KEYS,
} from 'hb-react/frontoffice/apps/Forum/constants';
import type { ConfigUrls } from 'hb-react/frontoffice/apps/Forum/types';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import type { Discussion } from 'hb-react/frontoffice/components/CardForum/types';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import ReactionsComments from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsComments';
import ReactionsLikes from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsLikes';
import ReactionsShares from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsShares';
import toggleLike from 'hb-react/shared/components/DesignSystem/Reactions/utils/toggleLike';
import useNavigatorShare from 'hb-react/shared/hooks/useNavigatorShare';

interface DiscussionReactionsProps {
  discussion: Discussion;
  configUrls: ConfigUrls;
}

const DiscussionReactions = ({
  discussion,
  configUrls,
}: DiscussionReactionsProps) => {
  const navigate = useNavigate();
  const {
    id,
    likes,
    title,
    comments: { total },
  } = discussion;
  const queryClient = useQueryClient();
  const { topicId } = useTopicContext();

  const sharedResourceUrl = `${
    window.location.origin
  }${configUrls.discussionUrl(id)}`;

  const { handleShare, shareUrl } = useNavigatorShare({
    data: {
      text: title,
      url: sharedResourceUrl,
    },
    onShare: () => {
      amplitude.track('Forum|DiscussionShared');
    },
  });

  const onClickComment = () => {
    navigate(configUrls.discussionUrl(id), { state: COMMENTS_SCROLL });
  };

  const onClickLike = async (data) => {
    const likeData = await toggleLike(data);

    queryClient.invalidateQueries({
      queryKey: [ITEMS_QUERY_KEYS.forum.items, topicId],
      refetchType: 'none',
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.discussion, id, topicId],
    });

    return likeData;
  };

  return (
    <Box>
      <Box display="block" mr={2}>
        <ReactionsLikes
          initialLikes={likes}
          showFullText={false}
          type="Forums::Discussion"
          onTogglePostLike={onClickLike}
        />
      </Box>
      <Box display="block" mr={2}>
        <ReactionsComments
          totalComments={total}
          showFullText={false}
          dataTestId="comment-forum-discussion"
          onClick={onClickComment}
        />
      </Box>
      {handleShare && (
        <ReactionsShares
          // @ts-expect-error typing issue between propTypes and jsdoc
          onClick={handleShare}
          shareUrl={shareUrl}
          showFullText={false}
        />
      )}
    </Box>
  );
};

export default DiscussionReactions;
